import React, { useState } from 'react';

import { ActionIcon, Card, Group, Text, useMantineTheme } from '@mantine/core';
import { IconDotsVertical } from '@tabler/icons-react';
import useWorkoutAssignment from 'hooks/workout/useWorkoutAssignment';
import type { WorkoutCalendarKiloClassType } from 'types';
import { readableTextColor } from 'utils/colors';

import WeekAssignAffordance from './WeekAssignAffordance';
import WeekEventStatusBadge from './WeekEventStatusBadge';
import WeekWorkoutChip from './WeekWorkoutChip';
import AssignWorkoutModal from '../AssignWorkoutModal';
import DeleteWorkoutModal from '../DeleteWorkoutModal';
import WorkoutAssignmentMenu from '../WorkoutAssignmentMenu';
import WorkoutPreviewModal from '../WorkoutPreviewModal';

const FALLBACK_CLASS_TYPE_COLOR = '#6b7280';
const FOOTER_ROW_MIN_HEIGHT = 36;

interface WeekEventCardProps {
  classType: WorkoutCalendarKiloClassType;
  date: string;
}

const WeekEventCard = ({ classType, date }: WeekEventCardProps): JSX.Element => {
  const theme = useMantineTheme();
  const { kiloClassTypeId, name, classCount, color, workout } = classType;
  const accent = color?.trim() || FALLBACK_CLASS_TYPE_COLOR;
  const fg = readableTextColor(accent);

  const [previewOpened, setPreviewOpened] = useState(false);
  const workoutAssignment = useWorkoutAssignment({
    kiloClassTypeId,
    kiloClassTypeName: name,
    date,
    workoutId: workout?.workoutId,
    isVisibleToAthletes: workout?.isVisibleToAthletes,
    view: 'week',
  });

  const handleCardClick = (): void => {
    if (!workout) {
      if (workoutAssignment.isAssignBlocked) return;
      workoutAssignment.openAssignModal();
    } else {
      setPreviewOpened(true);
    }
  };

  const cardClickable = workout != null || !workoutAssignment.isAssignBlocked;

  return (
    <>
      <Card
        p='xs'
        radius='md'
        data-cy='weekEventCard'
        onClick={handleCardClick}
        sx={{
          backgroundColor: accent,
          color: fg,
          cursor: cardClickable ? 'pointer' : 'not-allowed',
          transition: 'opacity 150ms ease, box-shadow 150ms ease',
          '&:hover': cardClickable ? { opacity: 0.9, boxShadow: theme.shadows.sm } : {},
        }}
      >
        <Group position='apart' noWrap spacing={4} align='flex-start'>
          <Text size='sm' fw={700} lineClamp={2} sx={{ color: fg }}>
            {name}
          </Text>
          <WeekEventStatusBadge assigned={!!workout} fg={fg} />
        </Group>

        <Text size='xs' sx={{ color: fg, opacity: 0.85 }}>
          {classCount} {classCount === 1 ? 'class' : 'classes'}
        </Text>

        {!workout && <WeekAssignAffordance color={fg} minHeight={FOOTER_ROW_MIN_HEIGHT} />}

        {workout && (
          <WeekWorkoutChip
            name={workout.name}
            visibleToAthletes={workout.isVisibleToAthletes}
            minHeight={FOOTER_ROW_MIN_HEIGHT}
            menu={
              <WorkoutAssignmentMenu
                canMutate={workoutAssignment.canMutate}
                hasWorkoutId={!!workout.workoutId}
                owner='classType'
                isVisibleToAthletes={workout.isVisibleToAthletes}
                isLocked={workout.hasResults}
                onToggleVisibility={workoutAssignment.toggleVisibility}
                onViewWhiteboard={workoutAssignment.viewWhiteboard}
                onEditWorkout={workoutAssignment.editWorkout}
                onChangeWorkout={workoutAssignment.changeWorkout}
                onDeleteWorkout={workoutAssignment.requestDelete}
                target={
                  <ActionIcon
                    variant='subtle'
                    size='sm'
                    aria-label='Workout assignment options'
                    onClick={e => e.stopPropagation()}
                    sx={{ color: theme.colors.gray[7] }}
                  >
                    <IconDotsVertical size={16} />
                  </ActionIcon>
                }
              />
            }
          />
        )}
      </Card>

      <AssignWorkoutModal
        opened={workoutAssignment.assignModalOpened}
        onClose={workoutAssignment.closeAssignModal}
        target={{ kind: 'classType', id: kiloClassTypeId, name }}
        date={date}
        defaultVisibleToAthletes={workout?.isVisibleToAthletes ?? true}
        view='week'
      />
      <DeleteWorkoutModal
        opened={workoutAssignment.deleteModalOpened}
        onClose={workoutAssignment.closeDeleteModal}
        onConfirm={workoutAssignment.confirmDelete}
        isLoading={workoutAssignment.deleteLoading}
        workoutId={workout?.workoutId ?? ''}
        workoutName={workout?.name}
      />
      <WorkoutPreviewModal
        opened={previewOpened}
        onClose={() => setPreviewOpened(false)}
        workoutId={workout?.workoutId ?? null}
        returnView='week'
      />
    </>
  );
};

export default WeekEventCard;
